import {
  Appointment,
  AppointmentCancellationReason,
  AppointmentCreateParams,
  AppointmentType,
  Member,
  Slot,
  User,
} from '@allara-health/source-health-client'
import { RoutePaths } from 'containers/Core/Routes'
import { useAlert } from 'context/AlertContext/AlertContextProvider'
import { useOnboardingContext } from 'context/OnboardingContext/OnboardingProvider'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import useAppointmentType from 'hooks/useAppointments/useAppointmentType'
import UseSourceAppointment from 'hooks/useAppointments/useSourceAppointment'
import useAppointmentTypeId from 'hooks/useAppointments/useAppointmentTypeId'
import useInsurance from 'hooks/useInsuranceCard'
import {
  useProviderMatchingData,
  useProviderMatchingEligibility,
} from 'hooks/useProviderMatching'
import useSearchParams from 'hooks/useSearchParams'
import useOnboardingData from 'hooks/useUser/useOnboardingData'
import useUser from 'hooks/useUser/useUser'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import { BookingEventSource } from 'utils/amplitude.types'
import { useIsInsurancePatient } from 'utils/program.utils'
import { useFlags } from 'launchdarkly-react-client-sdk'

export type SchedulingScreen =
  | 'appointmentTypeSelect'
  | 'location'
  | 'book'
  | 'confirm'
  | 'success'
  | 'insuranceInfo'

export type AmplitudeBookingMetadata = {
  source: BookingEventSource
  patient_action_id: string | null
  patient_action_definition: string | null
  resource_id: string | null
}

interface SchedulingContextInterface {
  dateTime?: Date
  timeZone?: string
  provider?: User
  isReschedule?: boolean
  rebookingFlowData: {
    provider: User
    appointmentType: AppointmentType
    dateTime: Date
    timeZone: string
  } | null
  amplitudeBookingMetadata: AmplitudeBookingMetadata
  isLoadingApptType: boolean
  appointmentType?: AppointmentType
  appointment?: Appointment
  currentScreen: SchedulingScreen
  selectedSlot?: Slot
  penaltyScore?: number
  penaltyReason?: string
  participantsChanged: boolean
  shouldShowInsuranceStep: boolean
  setAppointmentTypeId: (appointmentTypeId: string | undefined) => void
  setDateTime: (dateTime: Date) => void
  setTimeZone: (timeZone: string) => void
  nextScreen: () => void
  previousScreen: () => void
  bookAppointment: () => Promise<Appointment | null>
  setProvider: (provider?: User) => void
  setCurrentScreen: (screen: SchedulingScreen) => void
  setSelectedSlot: (slot?: Slot) => void
  setPenaltyScore: (penaltyScore?: number) => void
  setPenaltyReason: (penaltyReason?: string) => void
  setRebookingFlowData: (
    rebookingFlowData: {
      provider: User
      appointmentType: AppointmentType
      dateTime: Date
      timeZone: string
    } | null
  ) => void
  cancelAppointment: (
    cancellationReason: AppointmentCancellationReason,
    cancellationReasonText?: string
  ) => Promise<void>
  costConcernAppointment: () => Promise<void>
  rescheduleAppointment: () => Promise<Appointment | null>
  appointmentHoldTimeLeft: string
  expiredTimeOut: boolean
}

export const SchedulingContext = createContext<SchedulingContextInterface>(
  {} as SchedulingContextInterface
)

const determineAppointmentSubject = ({
  isPrescriptionCheckin,
  isInitialMdAppointmentType,
  isInitialRdAppointmentType,
  isReasonWeightLoss,
  conditionDisplayName,
}: {
  isPrescriptionCheckin: boolean
  isInitialMdAppointmentType: boolean
  isInitialRdAppointmentType: boolean
  isReasonWeightLoss: boolean
  conditionDisplayName: string | null
}) => {
  if (isPrescriptionCheckin) {
    return 'Medication follow up'
  } else if (
    (isInitialRdAppointmentType || isInitialMdAppointmentType) &&
    isReasonWeightLoss
  ) {
    return 'Weight management program'
  } else if (isInitialMdAppointmentType && conditionDisplayName) {
    return conditionDisplayName
  }
}

interface SchedulingProviderProps {
  appointmentId?: string
  initialAppointmentTypeId?: string
  initialScreen?: SchedulingScreen
  children: React.ReactNode
}

export const SchedulingProvider: React.FC<SchedulingProviderProps> = ({
  appointmentId,
  initialAppointmentTypeId,
  initialScreen = 'location',
  children,
}) => {
  const history = useHistory()
  const { setAlertText } = useAlert()
  const { source } = useSourceContext()
  const searchParams = useSearchParams()
  const [rebookingFlowData, setRebookingFlowData] = useState<{
    provider: User
    appointmentType: AppointmentType
    dateTime: Date
    timeZone: string
  } | null>(null)
  const isReschedule = useMemo(
    () => (appointmentId && !rebookingFlowData ? true : false),
    [appointmentId, rebookingFlowData]
  )
  const [
    amplitudeBookingMetadata,
    setAmplitudeBookingMetadata,
  ] = useState<AmplitudeBookingMetadata>({
    source: BookingEventSource.UNKNOWN,
    patient_action_id: null,
    patient_action_definition: null,
    resource_id: null,
  })
  /*
  this should only ever be called once per booking flow
  note that searchParams are excluded from the dependency array
  because they are not expected to change.
  this exists to track actions outside of the scheduling context that navigate the user into the booking flow
  */
  useEffect(() => {
    const amplitudeBookingMetadata = {
      source:
        (searchParams.get('source') as BookingEventSource) ??
        BookingEventSource.UNKNOWN,
      patient_action_id: searchParams.get('patient_action_id'),
      patient_action_definition: searchParams.get('patient_action_definition'),
      resource_id: searchParams.get('resource_id'),
    }
    setAmplitudeBookingMetadata(amplitudeBookingMetadata)
  }, [])

  const [appointmentTypeId, setAppointmentTypeId] = useState(
    initialAppointmentTypeId
  )
  const {
    appointment: previousAppointment,
    isLoading: isLoadingAppt,
  } = UseSourceAppointment(appointmentId)
  const {
    appointmentType: fetchedAppointmentType,
    isLoading: isLoadingApptType,
  } = useAppointmentType(appointmentTypeId)
  const [dateTime, _setDateTime] = useState<Date | undefined>(undefined)
  const [timeZone, _setTimeZone] = useState<string | undefined>(undefined)
  const [provider, setProvider] = useState<User | undefined>(undefined)
  const [selectedSlot, setSelectedSlot] = useState<Slot | undefined>(undefined)
  const [penaltyScore, setPenaltyScore] = useState<number | undefined>(
    undefined
  )
  const [penaltyReason, setPenaltyReason] = useState<string | undefined>(
    undefined
  )
  const [appointment, setAppointment] = useState<Appointment | undefined>(
    undefined
  )
  const [currentScreen, setCurrentScreen] = useState<SchedulingScreen>(
    isReschedule ? 'success' : initialScreen
  )
  const [appointmentType, setAppointmentType] = useState<
    AppointmentType | undefined
  >(undefined)
  const [participantsChanged, setParticipantsChanged] = useState<boolean>(false)
  const setDateTime = (dateTime: Date | undefined) => _setDateTime(dateTime)
  const setTimeZone = (timeZone: string | undefined) => _setTimeZone(timeZone)
  const { user, isLoading: isLoadingUser } = useUser()
  const isInsurancePatient = useIsInsurancePatient(user)
  const { insurance, isLoading: isLoadingInsurance } = useInsurance()
  const shouldShowInsuranceStep =
    isInsurancePatient &&
    (!insurance?.success || !user?.hasElationAccount) &&
    !isLoadingInsurance &&
    !isLoadingUser
  const { insuranceDetails } = useOnboardingContext()

  const formatTime = (minutes: number, seconds: number) => {
    return `${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const APPOINTMENT_HOLD_TIME_MINUTES = 8
  const APPOINTMENT_HOLD_TIME_SECONDS = 0
  const endTimeRef = useRef<Date>(new Date())
  const [timeLeft, setTimeLeft] = useState<string>(
    formatTime(APPOINTMENT_HOLD_TIME_MINUTES, APPOINTMENT_HOLD_TIME_SECONDS)
  )
  const [expiredTimeOut, setExpiredTimeOut] = useState(false)

  useEffect(() => {
    if (currentScreen === 'insuranceInfo' || currentScreen === 'confirm') {
      const updateCountdown = () => {
        const diff = Math.max(endTimeRef.current.getTime() - Date.now(), 0)
        const min = Math.floor(diff / 60000)
        const sec = Math.floor((diff % 60000) / 1000)

        if (min === 0 && sec === 0) {
          setExpiredTimeOut(true)
        }

        setTimeLeft(formatTime(min, sec))
      }

      updateCountdown()
      const interval = setInterval(updateCountdown, 1000)

      return () => {
        clearInterval(interval)
        setExpiredTimeOut(false)
      }
    }
  }, [currentScreen])

  const clearAppointmentData = useCallback(() => {
    setAppointment(undefined)
    setDateTime(undefined)
    setProvider(undefined)
  }, [])

  const nextScreen = useCallback(() => {
    switch (currentScreen) {
      case 'appointmentTypeSelect':
        setCurrentScreen('location')
        break
      case 'location':
        setCurrentScreen('book')
        break
      case 'book':
        insuranceDetails.current = undefined
        endTimeRef.current = new Date(
          Date.now() +
            APPOINTMENT_HOLD_TIME_MINUTES * 60000 +
            APPOINTMENT_HOLD_TIME_SECONDS * 1000
        )
        setTimeLeft(
          formatTime(
            APPOINTMENT_HOLD_TIME_MINUTES,
            APPOINTMENT_HOLD_TIME_SECONDS
          )
        )

        if (shouldShowInsuranceStep) {
          setCurrentScreen('insuranceInfo')
        } else {
          setCurrentScreen('confirm')
        }

        break
      case 'insuranceInfo':
        setCurrentScreen('confirm')
        break
      case 'confirm':
        setCurrentScreen('success')
        break
      case 'success':
        history.push(`${RoutePaths.DASHBOARD}?appointment=${appointment?.id}`)
        break
      default:
        break
    }
  }, [currentScreen, setCurrentScreen, appointment])

  const previousScreen = useCallback(() => {
    switch (currentScreen) {
      case 'book':
        setCurrentScreen('location')
        break
      case 'confirm':
        setCurrentScreen('book')
        break
      case 'insuranceInfo':
        setCurrentScreen('book')
        break
      case 'location':
        initialScreen === 'appointmentTypeSelect'
          ? setCurrentScreen('appointmentTypeSelect')
          : history.push(RoutePaths.DASHBOARD)
        break
      case 'appointmentTypeSelect':
        history.push(RoutePaths.DASHBOARD)
        break
      case 'success':
      default:
        break
    }
  }, [currentScreen, setCurrentScreen])

  const { onboardingCondition } = useProviderMatchingEligibility()

  const { appointmentTypeId: initialRdAppointmentType } = useAppointmentTypeId(
    'Initial RD'
  )
  const { appointmentTypeId: initialMdAppointmentType } = useAppointmentTypeId(
    'Initial MD'
  )

  const isInitialRdAppointmentType = useMemo(() => {
    return appointmentType?.id
      ? appointmentType.id === initialRdAppointmentType
      : false
  }, [appointmentType, initialRdAppointmentType])

  // Note: the useSchedulingContext hook used to get appointmentType unreliably calculates `isInitialMdAppointmentType`,
  // which is why we have to calculate it here.
  const isInitialMdAppointmentType = useMemo(() => {
    return appointmentType?.id
      ? appointmentType.id === initialMdAppointmentType
      : false
  }, [appointmentType, initialMdAppointmentType])

  const { conditionDisplayName } = useProviderMatchingData(onboardingCondition)
  const { onboardingData } = useOnboardingData()
  const { weightManagementPatientJourneyEnabled } = useFlags()

  const bookAppointment = useCallback(() => {
    if (!appointmentType || !timeZone || !dateTime || !provider) {
      setAlertText('Please refresh the page and try again', 'An Error Occurred')
      return Promise.resolve(null)
    }

    const appointmentSubject = determineAppointmentSubject({
      isPrescriptionCheckin:
        searchParams.get('source') === BookingEventSource.PRESCRIPTION_CHECKIN,
      isInitialMdAppointmentType,
      isInitialRdAppointmentType,
      isReasonWeightLoss:
        weightManagementPatientJourneyEnabled &&
        onboardingData?.what_brings_you_to_allara === 'weight_loss',
      conditionDisplayName,
    })

    const appointmentCreateParams: AppointmentCreateParams = {
      appointment_type: appointmentType.id,
      time_zone: timeZone,
      start_at: dateTime.toISOString(),
      participants: [{ participant: provider.id }],
      penalty_score: penaltyScore,
      penalty_reason: penaltyReason,
      ...(appointmentSubject && { subject: appointmentSubject }),
    }

    return source.scheduling.appointments
      .create(appointmentCreateParams)
      .then((appointment: Appointment) => {
        setAppointment(appointment)
        setCurrentScreen('success')
        return appointment
      })
  }, [
    source,
    appointmentType,
    timeZone,
    dateTime,
    provider,
    searchParams,
    isInitialMdAppointmentType,
    isInitialRdAppointmentType,
    weightManagementPatientJourneyEnabled,
    onboardingData,
    conditionDisplayName,
    penaltyScore,
    penaltyReason,
  ])

  const rescheduleAppointment = useCallback(() => {
    if (
      !appointmentType ||
      !appointment ||
      !timeZone ||
      !dateTime ||
      !provider
    ) {
      setAlertText('Please refresh the page and try again', 'An Error Occurred')
      return Promise.resolve(null)
    }

    // TODO: confirm its not identical with existing appointment?

    // Confirm the appointment types match
    const scheduledAppointmentTypeId =
      typeof appointment.appointment_type === 'string'
        ? appointment.appointment_type
        : appointment.appointment_type?.id

    if (scheduledAppointmentTypeId !== appointmentType.id) {
      setAlertText(
        'You cannot update the appointment type for an appointment. Please contact concierge for assistance',
        'Hold On'
      )
      return Promise.resolve(null)
    }

    // If new participant remove video call from appointment so a new one can be created
    // let video_call = appointment.video_call
    const currentParticipant = appointment.participants[0].participant
    const currentProviderId =
      typeof currentParticipant === 'string'
        ? currentParticipant
        : currentParticipant.id

    if (provider.id !== currentProviderId) {
      // video_call = null
      setParticipantsChanged(true)
    }

    return source.scheduling.appointments
      .update(appointment.id, {
        time_zone: timeZone,
        start_at: dateTime.toISOString(),
        duration: appointmentType.duration,
        participants: [{ participant: provider.id }],
        penalty_score: penaltyScore,
        penalty_reason: penaltyReason,
        // video_call,
      })
      .then((appointment: Appointment) => {
        setAppointment(appointment)
        setCurrentScreen('success')
        return appointment
      })
  }, [
    source,
    appointmentType,
    timeZone,
    dateTime,
    provider,
    penaltyScore,
    penaltyReason,
  ])

  const cancelAppointment = useCallback(
    async (
      cancellationReason: AppointmentCancellationReason,
      cancellationReasonText?: string
    ) => {
      if (!appointment || !appointment.member) {
        setAlertText(
          'Please refresh the page and try again',
          'An Error Occurred'
        )
        return Promise.resolve()
      }

      const cancelParams: {
        cancellation_reason: AppointmentCancellationReason
        custom_cancellation_reason_text?: string
      } = {
        cancellation_reason: cancellationReason,
      }

      if (cancellationReasonText && cancellationReasonText.trim()) {
        cancelParams.custom_cancellation_reason_text = cancellationReasonText.trim()
      }

      return source.scheduling.appointments
        .cancel(appointment.id, cancelParams)
        .then(() => {
          clearAppointmentData()
          setAmplitudeBookingMetadata({
            source: BookingEventSource.APPOINTMENT_CANCEL_DONE,
            patient_action_id: null,
            patient_action_definition: null,
            resource_id: null,
          })
        })
    },
    [source, appointment]
  )

  const costConcernAppointment = useCallback(async () => {
    if (!appointment || !appointment.member) {
      setAlertText('Please refresh the page and try again', 'An Error Occurred')
      return Promise.resolve()
    }

    return source.scheduling.appointments
      .createCostTask(appointment.id, {
        summary: 'Copay for Insurance Appointment',
        description: 'Patient awaiting additional cost info',
      })
      .then(() => {
        history.push(RoutePaths.DASHBOARD + '?from=scheduling')
      })
      .catch((error: Error) => {
        console.error('Error creating cost verification task:', error)
        setAlertText(
          'We could not process your cost verification request. Please contact concierge@allarahealth.com for support.',
          'There was an error processing your request'
        )
      })
  }, [appointment, history, setAlertText, source])

  // Set appointment and appointmentType when the previous
  useEffect(() => {
    if (
      previousAppointment &&
      !appointment &&
      currentScreen !== 'location' &&
      !rebookingFlowData
    ) {
      setAppointment(previousAppointment)
      setAppointmentType(
        previousAppointment.appointment_type as AppointmentType
      )
      setDateTime(new Date(previousAppointment.start_at))

      if (previousAppointment.participants.length) {
        setProvider(previousAppointment.participants[0].participant as User)
      }

      const member = previousAppointment.member as Member

      if (member.time_zone) {
        setTimeZone(member.time_zone)
      }
    }
  }, [previousAppointment, appointment])

  // Set appointmentType when loaded
  useEffect(() => {
    if (fetchedAppointmentType) {
      setAppointmentType(fetchedAppointmentType)
    }
  }, [fetchedAppointmentType])

  // Handle invalid appointment type
  useEffect(() => {
    if (appointmentTypeId && !isLoadingApptType && !fetchedAppointmentType) {
      history.push(RoutePaths.DASHBOARD)
    }
  }, [appointmentTypeId, fetchedAppointmentType, isLoadingApptType])

  // Handle invalid appointment
  useEffect(() => {
    if (appointmentId && !isLoadingAppt && !previousAppointment) {
      history.push(RoutePaths.DASHBOARD)
    }
  }, [appointmentId, previousAppointment, isLoadingAppt])

  // Handle polling if no zoom link yet
  useEffect(() => {
    let interval: NodeJS.Timeout

    if (appointment && !appointment.video_call) {
      let poll = 4
      interval = setInterval(() => {
        if (poll <= 0) {
          clearInterval(interval)
        } else {
          poll--
          source.scheduling.appointments
            .retrieve(appointment.id)
            .then((appt: Appointment) => {
              if (appt.video_call) {
                setAppointment(appt)
                clearInterval(interval)
              }
            })
        }
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [appointment])

  return (
    <SchedulingContext.Provider
      value={{
        dateTime,
        timeZone,
        provider,
        isReschedule,
        amplitudeBookingMetadata,
        currentScreen,
        isLoadingApptType,
        appointmentType,
        appointment,
        selectedSlot,
        penaltyScore,
        penaltyReason,
        participantsChanged,
        rebookingFlowData,
        shouldShowInsuranceStep,
        appointmentHoldTimeLeft: timeLeft,
        expiredTimeOut,
        setAppointmentTypeId,
        setDateTime,
        setTimeZone,
        setProvider,
        nextScreen,
        previousScreen,
        bookAppointment,
        setCurrentScreen,
        setSelectedSlot,
        setPenaltyScore,
        setPenaltyReason,
        cancelAppointment,
        costConcernAppointment,
        rescheduleAppointment,
        setRebookingFlowData,
      }}
    >
      {children}
    </SchedulingContext.Provider>
  )
}

export const useSchedulingContext = (): SchedulingContextInterface =>
  useContext(SchedulingContext)
